import {
  CellDoubleClickedEvent,
  CellKeyDownEvent,
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GridOptions,
  ICellEditorParams,
  ICellRendererParams,
  RowNode,
} from '@ag-grid-community/core'
import { HttpParams } from '@angular/common/http'
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormControl, ValidationErrors } from '@angular/forms'
import {
  EditableCheckboxRenderer,
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import {
  RootState,
  selectActiveSession,
  selectActiveSessionIdentifier,
  selectActiveSessionReconDate,
  selectActiveUserId,
  selectActiveWindowDate,
  selectIsInternalUser,
  setCommandsMetrics,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import {
  NgbActiveModal,
  NgbModal,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams, resizeColumns } from '@northfork/ag-grid'
import {
  InlineEditDataType,
  InlineEditorParams,
  NgGridComponents,
  getInlineEditColumnType,
} from '@northfork/ag-grid-angular'
import { ISODateFormat, toISOFormat } from '@northfork/basics'
import { InlineEditDisplayOptions, ToastService } from '@northfork/ng-basics'
import { isAfter } from 'date-fns'
import { Subject } from 'rxjs'
import { debounceTime, retry, take, takeUntil } from 'rxjs/operators'
import { AccountDetails } from '../../../model/single-transaction/account-details.model'
import { CreateEditTransactionRequest } from '../../../model/single-transaction/create-edit-transaction.model'
import { ExternalTransactionDetail } from '../../../model/single-transaction/external-transaction-detail'
import { ExternalUnmatchedTransaction } from '../../../model/single-transaction/external-unmatched-transaction'
import { TransactionModalInput } from '../../../model/single-transaction/popup-input.model'
import { SleeveSimpleAccounts } from '../../../model/single-transaction/sleeve-simple-accounts.model'
import { TransactionNote } from '../../../model/single-transaction/transaction-note'
import {
  ValidationExternalTransactionData,
  WireValidationView,
} from '../../../model/single-transaction/validation-view.model'
import {
  WireTransfer,
  WireTransferModel,
  wireTransferField,
} from '../../../model/single-transaction/wire-transfer/wire-transfer.model'
import {
  TranCategory,
  TranCategoryUrl,
} from '../../../model/transaction-category.model'
import {
  ErrorSeverity,
  ValidationInfo,
} from '../../../model/validation-response.model'
import { CopyService } from '../../../services/copy.service'
import { CreateEditTransactionService } from '../../../services/create-edit-transaction.service'
import { ModalFocusService } from '../../../services/modal-focus.service'
import { PasteService } from '../../../services/single-transaction/paste.service'
import {
  ACCOUNT_REQUIRED_ERROR,
  ACCOUNT_REQUIRED_FIELD_WARNING,
  SINGLE_DIALOG_EVENT_NAME,
  SINGLE_DIALOG_VALIDATION_EVENT_NAME,
  activateDialog,
  addWarningDetails,
  clearAllInvalidations,
  createMetadata,
  emptySelectedCellsOnValidKeyPress,
  fillHandleDoubleClickHandlerSingleDialog,
  focusFieldRowEditor,
  formatNumberWithThousandSeparator,
  getFieldValidatorFormatter,
  handleValidation,
  internalAssetIdFilterGridOptions,
  isDialogComponentInActive,
  singleDialogMetricsDetail,
} from '../../../util/common-utils'
import { TransactionConstants } from '../../../util/constants'
import {
  getCurrencyFromCusip,
  getCurrencyFromId,
} from '../../../util/currency-utils'
import {
  isEditModal,
  isExternalTransaction,
} from '../../../util/popup-input-util'
import { getRequestModeFromModalMode } from '../../../util/validation-response-util'
import {
  generateCashWireCreateMapping,
  generateCashWireEditMapping,
  generateCashWireExternalMapping,
} from '../../../util/wire-transfer-data-mapper-util'
import { Currency } from './../../../model/currency.model'
import { TransactionTypeLabel } from '../../../model/single-transaction/transaction'

@Component({
  selector: 'hs-wire-transfer',
  templateUrl: './wire-transfer.component.html',
  styleUrls: ['./wire-transfer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PasteService, CopyService],
})
export class WireTransferComponent implements OnInit, OnDestroy {
  // True while an aggregate account is selected but no concrete sub-account has been
  // picked yet. Fields stay editable; validation is deferred and edited rows are flagged
  // until the account is resolved.
  aggregateAccountUnresolved = false
  @Input() transInput?: TransactionModalInput

  private readonly colDefs: ColDef[] = this.createColumnDefs()
  gridOptions: GridOptions = this.createGridOptions()
  rowData: WireTransferModel[] = generateCashWireCreateMapping()

  getFieldValidatorFormatter = getFieldValidatorFormatter
  gridApi?: AgGridApi
  dateFormat = ISODateFormat
  accountDetails?: AccountDetails
  note = new FormControl('')
  rowNodeMap = new Map<string, RowNode>()
  transactionLoaded = false
  formSubmitting = false
  sessionId?: string
  sourceAccountId?: number
  sleeveSimpleAccount: SleeveSimpleAccounts[] = []
  userId?: number
  reconDate?: Date
  windowDate?: Date
  errorFields: string[] = []
  forbiddenErrors = new FormControl('')
  isValidating = false
  isDataValid = false
  isCellEditing = false
  noteWarning: ValidationErrors | null = null
  transaction?: WireTransfer
  externalTransaction?: ExternalUnmatchedTransaction
  externalTranTypeLabel?: TransactionTypeLabel
  tranNote?: TransactionNote
  cusip = ''
  originalTransaction?: WireTransfer
  executionId?: number
  originalCusip?: string
  externalAccountId?: number
  isExternalAccountSleeveSimple = false
  securityId?: number
  currencyList: Currency[] = []
  isInternalUser = false

  private readonly destroySubject = new Subject<void>()
  accountLoaded = true
  compId = 'wire-' + Math.random()
  activeInstances: NgbModalRef[] = []

  loadingText = TransactionConstants.LOAD_TRANSACTION
  errorText = ''
  errorDetails: unknown
  errorHeader: string | undefined
  title = 'Create Wire Transfer'
  private readonly destroySubject$ = new Subject<void>()

  constructor(
    private readonly createEditTransactionService: CreateEditTransactionService,
    public activeModal: NgbActiveModal,
    private readonly store: Store<RootState>,
    public toastService: ToastService,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
    private readonly modalService: NgbModal,
    private readonly elRef: ElementRef,
    private readonly telemetryService: TelemetryService,
    private readonly modalFocusService: ModalFocusService,
  ) {
    this.modalService.activeInstances
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(activeInstances => {
        this.activeInstances = activeInstances
      })
    this.store
      .select(selectActiveSessionIdentifier)
      .pipe(take(1))
      .subscribe((sessionId?: string) => {
        this.sessionId = sessionId
      })
    this.store
      .select(selectActiveSession)
      .pipe(take(1))
      .subscribe(session => {
        this.sourceAccountId = session?.loadedAccountIds[0]
        //this.setRowValueById(this.sourceAccountId + '', 'sourceAccountId')
      })
    this.store
      .select(selectActiveUserId)
      .pipe(take(1))
      .subscribe((userId?: number) => {
        this.userId = userId
      })
    this.store
      .select(selectIsInternalUser)
      .pipe(take(1))
      .subscribe(isInternalUser => {
        this.isInternalUser = isInternalUser ?? false
        this.gridApi?.api.onFilterChanged()
      })
    this.store
      .select(selectActiveSessionReconDate)
      .pipe(take(1))
      .subscribe((reconDate?: Date) => {
        this.reconDate = reconDate
        this.setRowValueById(toISOFormat(reconDate) + '', 'entryDate')
        this.setRowValueById(toISOFormat(reconDate) + '', 'sourcePostDate')
        this.setRowValueById(toISOFormat(reconDate) + '', 'destPostDate')
      })
    this.store
      .select(selectActiveWindowDate)
      .pipe(take(1))
      .subscribe(windowDate => {
        this.windowDate = windowDate
      })
  }

  ngOnDestroy(): void {
    this.pasteService.destroy()
    this.copyService.destroy()

    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  ngOnInit(): void {
    const startTime = Date.now()
    activateDialog(this.activeInstances, this.compId, this.elRef)

    this.modalFocusService
      .outsideFocus(this.elRef)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => this.disableGridFocus())

    this.note.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      if (this.gridApi) this.validate()
    })

    if (isEditModal(this.transInput) && this.sessionId) {
      this.title = this.transInput?.readOnly
        ? 'View Wire Transfer'
        : 'Edit Wire Transfer'
      this.plugWireTransaction(this.sessionId)
    } else if (
      this.transInput &&
      isExternalTransaction(this.transInput) &&
      this.sourceAccountId
    ) {
      this.title = 'Post Wire Transfer'
      this.plugExternalUnmatchedDataView(
        this.transInput.transactionId,
        this.sourceAccountId,
      )
    } else {
      this.isDataValid = false
      this.setupInitialData(this.rowData)
      if (this.sourceAccountId) {
        this.plugCreateDataView(this.sourceAccountId)
      }
    }

    const now = Date.now()
    const type = singleDialogMetricsDetail(this.transInput)
    this.telemetryService.emitCustomEvent(SINGLE_DIALOG_EVENT_NAME, {
      dialogName: 'Wire-' + type,
      durationMs: now - startTime,
    })
  }

  get colDef() {
    return this.colDefs
  }

  private createColumnDefs(): ColDef[] {
    return [
      {
        field: wireTransferField('isEditable'),
        hide: true,
      },
      {
        field: wireTransferField('errorMessage'),
        hide: true,
      },
      {
        field: wireTransferField('validityStatus'),
        headerName: '',
        valueGetter: () => '',
        cellClass: params => {
          return params.data.validityStatus === 'valid'
            ? 'mdi mdi-check-circle text-success'
            : params.data.validityStatus === 'invalid'
            ? 'mdi mdi-close-circle-outline text-danger'
            : 'mdi mdi-alert-circle-outline text-warning'
        },
        tooltipField: 'errorMessage',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessage != null && params.data.errorMessage != '',
        } as NfTooltipParams,
        maxWidth: heliosDefaultColWidth.ValidityStatusCol,
      },
      {
        field: wireTransferField('field'),
        headerName: 'Field',
        maxWidth: 140,
      },
      {
        field: wireTransferField('value'),
        headerName: 'Value',
        type: getInlineEditColumnType(),
        cellEditorParams: (params: ICellEditorParams) =>
          this.editInlineColumns(params),
        editable: (x: EditableCallbackParams) => this.editable(x),
        cellRenderer: undefined,
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellRendererSelector: params => {
          const isCheckboxRow = this.isCheckBoxCell(params)
          const component = isCheckboxRow
            ? EditableCheckboxRenderer
            : NgGridComponents.inlineEditDisplay
          return {
            component,
            params,
          }
        },
        valueFormatter: params =>
          formatNumberWithThousandSeparator(params.data.type, params.value),
      },
    ]
  }

  editable(params: EditableCallbackParams): boolean {
    const tableNode = params.api?.getRowNode(params.data['id'])
    if (tableNode) {
      this.rowNodeMap.set(params.data['id'], tableNode)
    }
    // Fields stay editable before an account is picked; validation is deferred instead, and
    // edited rows are flagged until the account is resolved (see onColumnValueChanged).
    return params.data.isEditable
  }

  isCheckBoxCell(params: ICellRendererParams): boolean {
    return params.data.id == 'destCleared' || params.data.id == 'sourceCleared'
  }

  /**
   * PVAL-25738: external users must not see the internal Asset ID row when
   * editing/viewing an existing or external transaction.
   */
  private get hideInternalAssetIdRows(): boolean {
    return (
      !this.isInternalUser &&
      (isEditModal(this.transInput) || isExternalTransaction(this.transInput))
    )
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      ...internalAssetIdFilterGridOptions(
        () => this.hideInternalAssetIdRows,
        () => this.rowNodeMap,
      ),
      onGridReady: event => {
        this.gridApi = event
        resizeColumns(event.api, event.columnApi)
        this.copyService.registerGrid(event.api)
        this.pasteService.registerGrid(event.api, event.columnApi)
      },
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        minWidth: heliosDefaultColWidth.ValidityStatusCol,
        sortable: false,
      },
      sideBar: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      suppressMultiRangeSelection: false,
      enableFillHandle: true,
      fillHandleDirection: 'y',
      onCellEditingStarted: () => {
        this.isCellEditing = true
      },
      onCellEditingStopped: () => {
        this.isCellEditing = false
      },
      onCellKeyDown: (event: CellKeyDownEvent) => {
        if (event.api.getEditingCells().length == 0) {
          emptySelectedCellsOnValidKeyPress(
            (event.event as KeyboardEvent).key,
            event.api,
          )
        }
      },
      getRowNodeId: ({ id }: WireTransferModel) => id,
      columnDefs: this.colDefs,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onFirstDataRendered: (params: any) => {
        params.api.sizeColumnsToFit()
      },
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        const element = event.event?.target as HTMLElement
        if (element.className == 'ag-fill-handle') {
          fillHandleDoubleClickHandlerSingleDialog(event.api)
        } else {
          event.api.startEditingCell({
            rowIndex: event.rowIndex ?? 0,
            colKey: event.column.getColId(),
          })
        }
      },
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
    }
  }

  onColumnValueChanged(event: CellValueChangedEvent) {
    if (
      event.newValue === event.oldValue ||
      event.column.getColId() != 'value'
    ) {
      return
    }

    const tradeEntryRow: WireTransferModel = event.data

    /* Category onChange event handling */
    switch (tradeEntryRow.id) {
      case 'sourcePostDate':
        this.clearUnclearTransaction(
          event.data.value,
          this.rowNodeMap.get('sourceCleared'),
        )
        break
      case 'destPostDate':
        this.clearUnclearTransaction(
          event.data.value,
          this.rowNodeMap.get('destCleared'),
        )
        break
      case 'sourceAccountId':
        this.loadAggAccountDetails(event.data.value)
        break
    }
    if (tradeEntryRow.id != 'sourceAccountId') {
      if (this.aggregateAccountUnresolved) {
        // No account yet: flag the edited field instead of validating it.
        addWarningDetails(event.node, ACCOUNT_REQUIRED_FIELD_WARNING)
        event.api.refreshCells({ force: true, rowNodes: [event.node] })
      } else {
        this.validate()
      }
    }
  }

  validate() {
    const startTime = Date.now()
    // Don't validate while an aggregate account is unresolved; there's no concrete
    // account to scope the request to yet (edited fields are flagged instead).
    if (this.aggregateAccountUnresolved) {
      return
    }
    this.isDataValid = false
    const validateView = this.createValidationView(
      this.accountDetails as AccountDetails,
    )
    this.isValidating = true
    this.createEditTransactionService
      .validateTransaction(validateView, TranCategoryUrl.DIRECT_TRANSFER)
      .subscribe(
        validationInfos => {
          this.handleValidationResponse(
            validationInfos.filter(
              validationInfo => validationInfo.level != ErrorSeverity.FORBIDDEN,
            ),
          )
          this.handleValidationResponseForbidden(
            validationInfos.filter(
              validationInfo => validationInfo.level == ErrorSeverity.FORBIDDEN,
            ),
          )
          this.isValidating = false
          const now = Date.now()
          const type = singleDialogMetricsDetail(this.transInput)
          this.telemetryService.emitCustomEvent(
            SINGLE_DIALOG_VALIDATION_EVENT_NAME,
            {
              dialogName: 'Wire-' + type,
              durationMs: now - startTime,
            },
          )
        },
        err => {
          this.errorHeader = TransactionConstants.VALIDATION_ERROR
          this.errorDetails = err.error
          this.isValidating = false
        },
      )
  }

  handleValidationResponseForbidden(validationInfos: ValidationInfo[]) {
    if (validationInfos.length == 0) return
    const forbiddenErrorsList: string[] = []
    validationInfos.forEach(validationInfo => {
      forbiddenErrorsList.push(validationInfo.message)
    })
    const validatorKey = 'forbiddenErrorsValidator'
    this.forbiddenErrors.markAsTouched()
    this.forbiddenErrors.setErrors({
      [validatorKey]: {
        message: forbiddenErrorsList,
      },
    })
    this.isDataValid = false
  }

  handleValidationResponse(validationInfos: ValidationInfo[]) {
    const errorMessages: Map<string, string[]> = new Map()
    const warningMessages: Map<string, string[]> = new Map()
    validationInfos.forEach(validationInfo => {
      if (validationInfo.level == ErrorSeverity.ERROR) {
        validationInfo.impactedFields.forEach(impactedField => {
          if (errorMessages.has(impactedField)) {
            errorMessages.get(impactedField)?.push(validationInfo.message)
          } else {
            errorMessages.set(impactedField, [validationInfo.message])
          }
        })
      } else {
        validationInfo.impactedFields.forEach(impactedField => {
          if (warningMessages.has(impactedField)) {
            warningMessages.get(impactedField)?.push(validationInfo.message)
          } else {
            warningMessages.set(impactedField, [validationInfo.message])
          }
        })
      }
    })
    this.isDataValid = errorMessages.size == 0
    this.rowNodeMap.forEach((value: RowNode) => {
      handleValidation(value, 'VALID', '')
    })
    this.note.setErrors(null)
    errorMessages.forEach((value: string[], key: string) => {
      let currentNode = this.rowNodeMap.get(key)
      if (key == 'securityId') currentNode = this.rowNodeMap.get('cusip')
      if (key == 'accountId')
        currentNode = this.rowNodeMap.get('sourceAccountId')
      if (currentNode) {
        handleValidation(currentNode, 'INVALID', value.join(' | '))
      } else if (key == 'transactionNote') {
        const validatorKey = 'transactionNoteValidator'
        this.note.markAsTouched()
        this.note.setErrors({
          [validatorKey]: {
            message: value,
          },
        })
      }
    })
    warningMessages.forEach((value: string[], key: string) => {
      let currentNode = this.rowNodeMap.get(key)
      if (key == 'securityId') currentNode = this.rowNodeMap.get('cusip')
      if (key == 'accountId')
        currentNode = this.rowNodeMap.get('sourceAccountId')
      if (currentNode && currentNode.data.errorMessage == '') {
        handleValidation(currentNode, 'WARNING', value.join(' | '))
      } else if (
        key == 'transactionNote' &&
        !errorMessages.has('transactionNote')
      ) {
        const validatorKey = 'transactionNoteValidator'
        this.noteWarning = {
          [validatorKey]: {
            message: value,
          },
        }
      }
    })
  }

  createValidationView(accountDetails: AccountDetails): WireValidationView {
    const view: WireValidationView = {
      userData: {
        transaction: this.prepareTransactionData(true),
        cusip: this.rowNodeMap.get('cusip')?.data.value,
      },
      accountDetails: accountDetails,
      requestMode: getRequestModeFromModalMode(this.transInput),
      windowDate: this.windowDate ?? new Date(),
    }

    if (this.note.value != '') {
      view.transactionNote = this.note.value
    }
    if (this.externalAccountId) {
      view.externalTransaction = {
        accountId: this.externalAccountId,
        isSleeveSimple: this.isExternalAccountSleeveSimple,
      } as ValidationExternalTransactionData
    }
    return view
  }

  /* Define dataType for all fields */
  editInlineColumns(params: ICellEditorParams): InlineEditorParams {
    switch (params.data.id) {
      case 'settleDate':
      case 'entryDate':
      case 'sourcePostDate':
      case 'destPostDate':
        return {
          dataType: InlineEditDataType.Date,
        }
      case 'sourceAccountId':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: this.sleeveSimpleAccount,
            bindValue: 'id',
            bindLabel: 'id',
            loading: this.sleeveSimpleAccount.length == 0,
          },
        }
      case 'destAccountId':
      case 'cusip':
        return {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        }
      case 'units':
        return {
          dataType: InlineEditDataType.Number,
          options: {
            format: '1.2-16',
          },
          disablePopover: true,
        }
      case 'sourceCleared':
      case 'destCleared':
        return {
          dataType: InlineEditDataType.Checkbox,
        }

      default:
        return {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        }
    }
  }

  setupInitialData(rowData: WireTransferModel[]) {
    rowData.forEach(element => {
      if (element.validityStatus === 'invalid') {
        this.errorFields.push(element.id)
      }
      this.isDataValid = false
      const tableNode = this.rowNodeMap.get(element.id)

      if (tableNode) {
        if (
          tableNode.data.validityStatus == 'invalid' &&
          element.validityStatus == 'valid'
        ) {
          this.errorFields.splice(
            this.errorFields.indexOf(tableNode.data['id']),
            1,
          )
          if (this.errorFields.length == 0) {
            this.isDataValid = true
          }
        }
        tableNode.data = element
        this.gridApi?.api.redrawRows({ rowNodes: [tableNode] })
      }
    })
  }

  private plugWireTransaction(sessionId: string) {
    const transactionId = this.transInput?.transactionId
    const securityId = this.transInput?.securityId
    const accountId = this.sourceAccountId
    if (transactionId == null || securityId == null || accountId == null) {
      return
    }

    this.transactionLoaded = false
    const params = new HttpParams()
      .set('accountId', accountId.toString())
      .set('securityId', securityId.toString())
    this.createEditTransactionService
      .loadTransactionDetail(
        sessionId,
        transactionId,
        params,
        TranCategoryUrl.DIRECT_TRANSFER,
      )
      .subscribe(
        async trans => {
          const transaction = trans.transactionData as WireTransfer
          this.tranNote = trans.note
          this.originalTransaction = transaction
          this.accountDetails = trans.accountDetails
          if (
            this.accountDetails?.isAggregate &&
            isEditModal(this.transInput)
          ) {
            await this.loadAndMapAccountDetailsForSleeve(transaction.accountId)
          }
          this.setAccountDisplayName()
          this.transaction = transaction
          this.securityId = transaction.securityId
          if (trans.securityMasterData) {
            this.cusip = trans.securityMasterData.cusip
          }
          this.rowData = generateCashWireEditMapping(transaction, this.cusip)
          this.note.setValue(this.tranNote?.note ?? '')
          this.transactionLoaded = true
        },
        err => {
          this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
          this.errorDetails = err.error
        },
      )
  }

  plugExternalUnmatchedDataView(
    transactionId?: string,
    accountId?: number,
  ): Promise<[ExternalTransactionDetail, Currency[]]> | undefined {
    if (transactionId && accountId) {
      const apiPromise = this.getPromisesExternal(accountId, transactionId)
      apiPromise
        .then(async ([externalTransactionDetail, currencyList]) => {
          const extTransaction = externalTransactionDetail.externalTransaction
          this.currencyList = currencyList
          this.executionId = extTransaction.executionId
          this.externalAccountId = extTransaction.accountId
          this.externalTransaction = extTransaction
          this.externalTranTypeLabel = extTransaction.transactionTypeLabel
          this.originalCusip =
            extTransaction.cusip ?? extTransaction.alternateId
          this.accountDetails = externalTransactionDetail.accountDetails
          if (
            this.accountDetails?.isAggregate &&
            this.accountDetails.accountId != this.externalAccountId
          ) {
            this.isExternalAccountSleeveSimple = true
            this.fetchSleeveSimples(this.accountDetails.accountId)
            await this.loadAndMapAccountDetailsForSleeve(this.externalAccountId)
          }
          this.rowData = generateCashWireExternalMapping(
            this.externalTransaction,
            accountId.toString(),
            this.isExternalAccountSleeveSimple,
          )
          this.setAccountDisplayName()
          const cusip = getCurrencyFromId(
            extTransaction?.currencyId,
            currencyList,
          )?.cusip
          if (cusip != undefined) {
            this.setRowValueById(cusip, 'cusip')
          }
          this.setupInitialData(this.rowData)
          if (this.gridApi) {
            this.validate()
          }
          this.transactionLoaded = true
        })
        .catch(err => {
          this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
          this.errorDetails = err.error
        })
      return apiPromise
    }
    return undefined
  }

  plugCreateDataView(
    accountId?: number,
  ): Promise<[AccountDetails, Currency[]]> | undefined {
    if (accountId) {
      this.errorDetails = ''
      const apiPromise = this.getPromisesCreate(accountId)
      apiPromise
        .then(([accountDetails, currencyList]) => {
          this.currencyList = currencyList
          this.accountDetails = accountDetails
          this.setAccountDisplayName()
          if (this.gridApi) {
            this.validate()
          }
          this.transactionLoaded = true
        })
        .catch(err => {
          this.errorDetails = err.error
        })
      return apiPromise
    }
    return undefined
  }

  getPromisesCreate(accountId: number): Promise<[AccountDetails, Currency[]]> {
    return Promise.all([
      this.createEditTransactionService
        .loadAccountDetails(accountId)
        .toPromise(),
      this.createEditTransactionService.loadCurrencies().toPromise(),
    ])
  }

  getPromisesExternal(
    accountId: number,
    transactionId: string,
  ): Promise<[ExternalTransactionDetail, Currency[]]> {
    return Promise.all([
      this.createEditTransactionService
        .loadExternalTransactionDetail(Number(transactionId), accountId)
        .toPromise(),
      this.createEditTransactionService.loadCurrencies().toPromise(),
    ])
  }

  setRowValueById(value: string, id: string) {
    const rowField = this.rowData.find(row => row.id == id)
    if (rowField) {
      rowField.value = value
    }
  }

  private prepareTransactionData(isValidate: boolean): WireTransfer {
    let createdDate = new Date().toISOString()
    if (isEditModal(this.transInput) && this.originalTransaction?.createdDate) {
      createdDate = new Date(
        this.originalTransaction?.createdDate,
      ).toISOString()
    }
    const transactionData: WireTransfer = {
      sourcePostDate: this.rowNodeMap.get('sourcePostDate')?.data.value,
      destPostDate: this.rowNodeMap.get('destPostDate')?.data.value,
      units: Number(this.rowNodeMap.get('units')?.data.value),
      tranType: 'DIRECT_CASH_TRANSFER',
      createdDate: createdDate,
      active: true,
      userId: this.userId ?? 0,
      sourceId: TransactionConstants.HELIOS_SOURCE_ID,
      accountId: this.accountDetails?.accountId ?? 0,
      entryDate: this.rowNodeMap.get('entryDate')?.data.value,
      settleDate: this.rowNodeMap.get('entryDate')?.data.value,
      postDate: this.rowNodeMap.get('sourcePostDate')?.data.value,
      modifiedDate: new Date().toISOString(),
      revisionId: isEditModal(this.transInput)
        ? (this.originalTransaction?.revisionId ?? 0) + 1
        : 1,
      dataMap: this.originalTransaction?.dataMap,
      sourceAccountId: Number(
        this.rowNodeMap.get('sourceAccountId')?.data.value,
      ),
    }
    //Do not send securityId to validate Cusip
    if (!isValidate) {
      if (!isEditModal(this.transInput)) {
        const cusip = this.rowNodeMap.get('cusip')?.data.value
        const currency = getCurrencyFromCusip(cusip, this.currencyList)
        if (currency) {
          transactionData.securityId = currency.cashSecurityId
        }
      } else {
        transactionData.securityId = this.securityId ?? -1
      }
    }

    if (this.rowNodeMap.get('destAccountId')?.data.value) {
      transactionData.destAccountId = Number(
        this.rowNodeMap.get('destAccountId')?.data.value,
      )
    }

    if (this.originalTransaction != null) {
      transactionData.transactionId = this.originalTransaction.transactionId
      transactionData.legacyOrigTranId =
        this.originalTransaction.legacyOrigTranId
      transactionData.legacyTranId = this.originalTransaction.legacyTranId

      if (this.originalTransaction?.sourceClearedDate) {
        transactionData.sourceClearedDate = new Date(
          this.originalTransaction.sourceClearedDate,
        ).toISOString()
      }
      if (this.originalTransaction?.destClearedDate) {
        transactionData.destClearedDate = new Date(
          this.originalTransaction.destClearedDate,
        ).toISOString()
      }
    } else {
      if (this.rowNodeMap.get('sourceCleared')?.data.value) {
        transactionData.sourceClearedDate = new Date().toISOString()
      }
      if (this.rowNodeMap.get('destCleared')?.data.value) {
        transactionData.destClearedDate = new Date().toISOString()
      }
    }
    if (!isValidate && this.originalTransaction?.typeLabelId) {
      transactionData.typeLabelId = this.originalTransaction.typeLabelId
    }
    return transactionData
  }

  save() {
    if (!this.isDataValid || !this.sessionId) {
      return
    }
    this.store.dispatch(
      setCommandsMetrics({
        command: 'wireTransfer',
        time: Date.now(),
      }),
    )
    this.formSubmitting = true

    this.loadingText = TransactionConstants.SAVE_TRANSACTION
    const tranCommand: CreateEditTransactionRequest = {
      transaction: this.prepareTransactionData(false),
      tranCategory: TranCategory.DIRECT_TRANSFER,
      metadata: createMetadata(
        this.reconDate ?? new Date(),
        this.rowNodeMap.get('cusip')?.data.value,
      ),
    }
    if (this.note.value != '') {
      tranCommand.transactionNote = { newValue: this.note.value }
    }

    if (this.originalTransaction?.transactionId) {
      tranCommand.notes =
        TransactionConstants.HELIOS_DIRECT_CASH_TRANSFER_SAVE_NOTE_CW_EDIT
      this.createEditTransactionService
        .editTransaction(
          this.sessionId,
          this.originalTransaction.transactionId,
          tranCommand,
        )
        .subscribe(
          () => {
            this.toastService.success(
              'Clearwater Transaction edit command successfully submitted.',
            )
            this.formSubmitting = false
            this.closeModal()
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    } else {
      if (isExternalTransaction(this.transInput)) {
        tranCommand.notes =
          TransactionConstants.HELIOS_DIRECT_CASH_TRANSFER_SAVE_NOTE_EXTERNAL
        tranCommand.extTran = {
          externalAccountId: this.externalAccountId ?? 0,
          executionId: this.executionId ?? 0,
          externalTranId: Number(this.transInput?.transactionId),
          originalCusip: this.originalCusip,
          status: this.transInput?.externalTransactionStatus,
        }

        if (this.externalTranTypeLabel?.id) {
          tranCommand.transaction.typeLabel = {
            id: this.externalTranTypeLabel.id,
            label: this.externalTranTypeLabel.label,
            description: this.externalTranTypeLabel.description,
          }
        }

        this.createEditTransactionService
          .saveExternalMatch(this.sessionId, tranCommand)
          .subscribe(
            () => {
              this.toastService.success(
                'External Transaction command successfully submitted.',
              )
              this.formSubmitting = false
              this.closeModal()
            },
            error => {
              this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
              this.errorDetails = error.error
              this.formSubmitting = false
            },
          )
      } else {
        tranCommand.notes =
          TransactionConstants.HELIOS_DIRECT_CASH_TRANSFER_SAVE_NOTE_CW_CREATE
        this.createEditTransactionService
          .createTransaction(this.sessionId, tranCommand)
          .subscribe(
            () => {
              this.toastService.success(
                'Clearwater Transaction create command successfully submitted.',
              )
              this.formSubmitting = false
              this.closeModal()
            },
            error => {
              this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
              this.errorDetails = error.error
              this.formSubmitting = false
            },
          )
      }
    }
  }

  clearUnclearTransaction(postDate: string, rowNode?: RowNode) {
    if (
      isAfter(
        new Date(postDate),
        this.reconDate
          ? new Date(this.reconDate as unknown as string)
          : new Date(),
      )
    ) {
      rowNode?.setDataValue('value', false)
    } else {
      rowNode?.setDataValue('value', true)
    }
  }

  private closeModal() {
    this.activeModal.close()
  }

  fetchSleeveSimples(accountId: number) {
    this.errorDetails = ''
    const sleeveSimples$ = this.createEditTransactionService
      .fetchSubAccounts(accountId)
      .pipe(retry(2))
    sleeveSimples$.subscribe(
      account => {
        this.sleeveSimpleAccount = account
        // Open the picker only once its options exist (opening earlier leaves the
        // select stuck loading). Skip when an external post already filled the account.
        if (
          this.aggregateAccountUnresolved &&
          this.gridApi?.api &&
          !this.isExternalAccountSleeveSimple
        ) {
          focusFieldRowEditor(this.gridApi.api, 'sourceAccountId')
        }
      },
      err => {
        this.errorHeader = TransactionConstants.ERROR_LOAD_SLEEVE_ACCOUNT
        this.errorDetails = err.error
      },
    )
  }

  async loadAggAccountDetails(accountId: number) {
    await this.loadAndMapAccountDetailsForSleeve(accountId)
    // A concrete account is now resolved; clear the account-unresolved marks and validate.
    this.aggregateAccountUnresolved = false
    clearAllInvalidations(this.gridApi?.api)
    if (this.gridApi) {
      this.validate()
    }
  }

  setAccountDisplayName() {
    const accountRowField = this.rowData?.find(
      row => row.id == 'sourceAccountId',
    )
    // Clear an aggregate account and force a sub-account pick, unless a sleeve-simple post
    // already resolved it to its concrete sub-account.
    if (
      this.accountDetails?.isAggregate &&
      !isEditModal(this.transInput) &&
      !this.isExternalAccountSleeveSimple
    ) {
      if (accountRowField) {
        accountRowField.isEditable = true
        accountRowField.value = ''
        accountRowField.validityStatus = 'invalid'
        accountRowField.errorMessage = ACCOUNT_REQUIRED_ERROR
      }
      this.aggregateAccountUnresolved = true
      const accountNode = this.rowNodeMap.get('sourceAccountId')
      if (accountNode) {
        accountNode.data.validityStatus = 'invalid'
        accountNode.data.errorMessage = ACCOUNT_REQUIRED_ERROR
      }
      this.gridApi?.api.refreshCells({ force: true })
      this.fetchSleeveSimples(this.accountDetails?.accountId)
      this.resetAggAccountDetails()
    } else {
      if (accountRowField) {
        accountRowField.value = this.accountDetails?.accountId + ''
      }
    }
  }

  resetAggAccountDetails() {
    this.sourceAccountId = undefined
    this.accountDetails = undefined
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }

  async loadAndMapAccountDetailsForSleeve(accountId: number) {
    this.accountDetails = await this.createEditTransactionService
      .loadAccountDetails(accountId)
      .toPromise()
    this.sourceAccountId = this.accountDetails?.accountId
  }

  @HostListener('click') dialogClicked() {
    if (isDialogComponentInActive(this.compId)) {
      activateDialog(this.activeInstances, this.compId, this.elRef)
    }
  }

  getComponentId() {
    return this.compId
  }
}
